/**
 * useUpdateCorrectionCategory — direct UPDATE on `mcp_corrections.category`.
 *
 * RLS already restricts UPDATE to contractor_admin / super_admin in the
 * row's org (migration 20260501000009 §mcp_corrections_update). The
 * immutability trigger (tg_mcp_corrections_immutable_match) explicitly
 * permits `category` mutations alongside `is_active` and `reason`.
 *
 * No business-logic enforcement client-side beyond the enum gate Zod
 * already enforces — RLS + trigger are the authority.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'
import type { McpCorrectionCategory }                           from '@/types/mcpCorrection'

interface UpdateVars {
  correctionId: string
  nextCategory: McpCorrectionCategory
}

export interface UpdateCorrectionResult {
  id:       string
  category: McpCorrectionCategory
}

export function useUpdateCorrectionCategory(): UseMutationResult<UpdateCorrectionResult, AppError, UpdateVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<UpdateCorrectionResult, AppError, UpdateVars>({
    mutationFn: async ({ correctionId, nextCategory }): Promise<UpdateCorrectionResult> => {
      const { data, error } = await supabase
        .from('mcp_corrections')
        .update({ category: nextCategory })
        .eq('id', correctionId)
        .select('id, category')
        .single()

      if (error) {
        const code = mapPgErrorCode(error.code)
        throw new AppError({
          code,
          message:     error.message,
          userMessage: userMessageFor(code, error.message),
          status:      403,
        })
      }
      return data as unknown as UpdateCorrectionResult
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mcp-corrections'] })
    },
  })
}

function mapPgErrorCode(pgCode: string | undefined): string {
  switch (pgCode) {
    case '42501':    return 'FORBIDDEN'
    case 'PGRST116': return 'NOT_FOUND'
    default:         return 'UPDATE_FAILED'
  }
}

function userMessageFor(code: string, fallback: string): string {
  switch (code) {
    case 'FORBIDDEN': return 'You do not have permission to reclassify this correction.'
    case 'NOT_FOUND': return 'This correction could not be found.'
    default:          return fallback || 'Could not update the correction category.'
  }
}
